import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, Maximize, Settings, Eye, Clock, Search } from 'lucide-react';

const VideoDemo: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const demoFeatures = [
    {
      icon: Search,
      title: "Smart Search",
      description: "Find concepts across all your videos instantly"
    },
    {
      icon: Eye,
      title: "Visual Insights",
      description: "See key moments and topics highlighted"
    },
    {
      icon: Clock,
      title: "Time Stamps",
      description: "Jump directly to relevant sections"
    }
  ];

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        // Request fullscreen on the video element to maintain aspect ratio
        videoRef.current.requestFullscreen();
      }
    }
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('ended', () => setIsPlaying(false));
      
      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('ended', () => setIsPlaying(false));
      };
    }
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="space-y-6"
    >
      {/* Video Player */}
      <div className="relative bg-gray-900 rounded-2xl overflow-hidden shadow-xl border border-white/20">
        {/* Video Element */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 relative" style={{ aspectRatio: '3376/1880' }}>
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            src="https://gdmk15qmxb4r4y2j.public.blob.vercel-storage.com/Quellect%20Demo.mov"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          
          {/* Play Button Overlay */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <button
                onClick={togglePlay}
                className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm hover:bg-white/30 transition-colors"
              >
                <Play className="w-10 h-10 ml-1 text-white" />
              </button>
            </div>
          )}

          {/* Video Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="flex items-center">
              <div className="flex items-center space-x-3 flex-[6]">
                <button
                  onClick={togglePlay}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-4 h-4 text-white" />
                  ) : (
                    <Play className="w-4 h-4 text-white ml-0.5" />
                  )}
                </button>
                
                <div 
                  className="flex-1 h-1 bg-white/30 rounded-full cursor-pointer"
                  onClick={handleSeek}
                >
                  <div 
                    className="h-full bg-white rounded-full transition-all duration-100"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  ></div>
                </div>
                
                <span className="text-white text-sm min-w-[60px] text-right pr-3">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
              
              <div className="flex items-center space-x-2 flex-[1] justify-center">
                <div className="relative">
                  <button 
                    onClick={() => setShowVolume(!showVolume)}
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    {isMuted ? (
                      <Volume2 className="w-4 h-4 text-white" />
                    ) : (
                      <Volume2 className="w-4 h-4 text-white" />
                    )}
                  </button>
                  
                  {/* Volume Panel */}
                  {showVolume && (
                    <div className="absolute bottom-12 right-0 bg-gray-800 rounded-lg p-3 shadow-xl border border-gray-700 min-w-[60px]">
                      <div className="flex flex-col items-center space-y-2">
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={1 - volume}
                          onChange={(e) => {
                            const newVolume = 1 - parseFloat(e.target.value);
                            setVolume(newVolume);
                            if (videoRef.current) {
                              videoRef.current.volume = newVolume;
                            }
                          }}
                          className="h-24 w-4 rounded-full cursor-pointer appearance-none"
                          style={{
                            WebkitAppearance: 'slider-vertical',
                            transform: 'rotate(180deg)'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <button 
                  onClick={toggleSettings}
                  className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <Settings className="w-4 h-4 text-white" />
                </button>
                <button 
                  onClick={toggleFullscreen}
                  className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <Maximize className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="absolute bottom-16 right-4 bg-gray-800 rounded-lg p-3 shadow-xl border border-gray-700 min-w-[200px]">
              <div className="space-y-3">

                <div>
                  <label className="text-white text-sm font-medium">Playback Speed</label>
                  <select
                    onChange={(e) => {
                      if (videoRef.current) {
                        videoRef.current.playbackRate = parseFloat(e.target.value);
                      }
                    }}
                    className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600"
                    defaultValue="1"
                  >
                    <option value="0.5">0.5x</option>
                    <option value="0.75">0.75x</option>
                    <option value="1">1x</option>
                    <option value="1.25">1.25x</option>
                    <option value="1.5">1.5x</option>
                    <option value="2">2x</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Video Title Bar */}
        {/* <div className="px-4 py-3 bg-gray-800 border-t border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Quellect Demo</h4>
              <p className="text-gray-400 text-sm">AI-powered video search and insights</p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="px-2 py-1 bg-primary-600 text-white text-xs rounded-full">
                AI Processed
              </span>
            </div>
          </div>
        </div> */}
      </div>

      {/* Demo Features */}
      {/* <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white text-center">
          See Quellect in Action
        </h3>
        
        <div className="grid grid-cols-1 gap-4">
          {demoFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-white/10 transition-colors backdrop-blur-sm"
            >
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-medium text-white">{feature.title}</h4>
                <p className="text-sm text-white/80">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div> */}

      {/* Interactive Demo Button */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="text-center"
      >
        <button
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="btn-primary w-full group relative overflow-hidden"
        >
          <span className="relative z-10">Try Interactive Demo</span>
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600"
            initial={{ x: '-100%' }}
            animate={{ x: isHovered ? '0%' : '-100%' }}
            transition={{ duration: 0.3 }}
          />
        </button>
        
        <p className="text-xs text-white/60 mt-2">
          Experience Quellect's AI-powered search and insights
        </p>
      </motion.div> */}
    </motion.div>
  );
};

export default VideoDemo;
